import { Injectable } from '@angular/core';
import { API, graphqlOperation } from 'aws-amplify';
import { observable, Observable } from 'rxjs';

// §9.7 — live in-app account notice for one user (e.g. a contact method was reclaimed).
const ON_ACCOUNT_NOTICE = `
    subscription OnAccountNotice($userId: ID!) {
        onAccountNotice(userId: $userId) {
            userId
            sk
            kind
            contactValue
            at
        }
    }
`;

const ON_NEW_MESSAGE = `
    subscription OnNewMessage($userId: ID!) {
        onNewMessage(userId: $userId) {
            id
            chatId
            userId
            senderId
            senderName
            body
            messageType
            attachments {
                url
                fileName
                fileType
                fileSize
            }
            replyTo {
                messageId
                senderId
                senderName
                body
                attachments {
                    url
                    fileName
                    fileType
                    fileSize
                }
            }
            system
            unreadCounts {
                userId
                chatId
                unreadCount
            }
            status
            createdAt
            reactions {
                emoji
                userId
                userName
                reactedAt
            }
        }
    }
`;

const ON_MESSAGE_EDITED = `
    subscription OnMessageEdited($chatId: ID!) {
        onMessageEdited(chatId: $chatId) {
            messageId
            chatId
            body
            attachments {
                url
                fileName
                fileType
                fileSize
            }
        }
    }
`;

const ON_MESSAGE_DELETED = `
    subscription OnMessageDeleted($chatId: ID!) {
        onMessageDeleted(chatId: $chatId) {
            chatId
            messageId
        }
    }
`;

const ON_TYPING_INDICATOR = `
    subscription OnTypingIndicator($chatId: ID!) {
        onTypingIndicator(chatId: $chatId) {
            chatId
            userId
            userName
            isTyping
        }
    }
`;

const TRIGGER_TYPING_INDICATOR = `
    mutation TriggerTypingIndicator(
        $chatId: ID!, 
        $userId: ID!,
        $userName: String!,
        $isTyping: Boolean!
    ) {
        triggerTypingIndicator(
            chatId: $chatId,
            userId: $userId,
            userName: $userName,
            isTyping: $isTyping
        ) {
            chatId
            userId
            userName
            isTyping
        }
    }
`;

@Injectable({
    providedIn: 'root'
})
export class AppSyncService {
    constructor() { }

    /**
     * §9.7 — Subscribe to live account notices for a user (contact-method reclaimed, etc.).
     * @param userId - the user to receive notices
     * @returns Observable that emits each account notice
     */
    subscribeToAccountNotice(userId: string): Observable<any> {
        return new Observable((observer) => {
            const graphqlResult = API.graphql(
                graphqlOperation(ON_ACCOUNT_NOTICE, { userId })
            ) as any;

            if (graphqlResult && typeof graphqlResult.subscribe === 'function') {
                const subscription = graphqlResult.subscribe({
                    next: (data: any) => {
                        const notice = data?.value?.data?.onAccountNotice;
                        if (notice) {
                            observer.next(notice);
                        }
                    },
                    error: (error: any) => {
                        console.error('AppSync AccountNotice Error: ', error);
                        observer.error(error);
                    },
                    complete: () => observer.complete()
                });

                return () => {
                    if (subscription && subscription.unsubscribe) {
                        subscription.unsubscribe();
                    }
                };
            } else {
                observer.error(new Error('Subscription failed: Invalid response type'));
            }
        });
    }

    /**
     * Subscribe to new messages for specific chat
     * @params userId - The user ID to subscribe to
     * @returns Observable that emits new messages
     */

    subscribeToNewMessages(userId: string): Observable<any> {
        return new Observable((observer) => {
            // Cast to any to handle the subscription type
            const graphqlResult = API.graphql(
                graphqlOperation(ON_NEW_MESSAGE, { userId })
            ) as any;

            // Check if it's an Observable (subscription) or Promise (query/mutation)
            if (graphqlResult && typeof graphqlResult.subscribe === 'function') {
                const subscription = graphqlResult.subscribe({
                    next: (data: any) => {
                        console.log('📨 AppSync Subscription Data: ', data);
                        const message = data?.value?.data?.onNewMessage;
                        if (message) {
                            observer.next(message);
                        }
                    },
                    error: (error: any) => {
                        console.error('AppSync Subscription Error: ', error);
                        observer.error(error);
                    },
                    complete: () => {
                        console.log('AppSync subscription completed');
                        observer.complete();
                    }
                });

                // Return cleanup function
                return () => {
                    if (subscription && subscription.unsubscribe) {
                        subscription.unsubscribe();
                    }
                };
            } else {
                observer.error(new Error('Subscription failed: Invalid response type'));
            }
        });
    }

    /**
     * Subscribe to message edits for a specific chat
     */
    subscribeToMessageEdited(chatId: string): Observable<any> {
        return new Observable((observer) => {
            const graphqlResult = API.graphql(
                graphqlOperation(ON_MESSAGE_EDITED, { chatId })
            ) as any;

            if (graphqlResult && typeof graphqlResult.subscribe === 'function') {
                const subscription = graphqlResult.subscribe({
                    next: (data: any) => {
                        const edit = data?.value?.data?.onMessageEdited;
                        if (edit) observer.next(edit);
                    },
                    error: (err: any) => observer.error(err),
                    complete: () => observer.complete()
                });
                return () => {
                    if (subscription?.unsubscribe) subscription.unsubscribe();
                };
            }
            observer.error(new Error('Message edit subscription failed: Invalid response type'));
        });
    }

    subscribeToMessageDeleted(chatId: string): Observable<any> {
        return new Observable((observer) => {
            const graphqlResult = API.graphql(
                graphqlOperation(ON_MESSAGE_DELETED, { chatId })
            ) as any;

            if (graphqlResult && typeof graphqlResult.subscribe === 'function') {
                const subscription = graphqlResult.subscribe({
                    next: (data: any) => {
                        console.log('🔄 data: ', data);
                        const deleted = data?.value?.data?.onMessageDeleted;
                        if (deleted) observer.next(deleted);
                    },
                    error: (err: any) => observer.error(err),
                    complete: () => observer.complete()
                });
                return () => {
                    if (subscription?.unsubscribe) subscription.unsubscribe();
                };
            }
            observer.error(new Error('Message delete subscription failed: Invalid response type'));
        });
    }
    
    /**
     * Trigger typing indicator mutation
     * @params chatId - The chat ID 
     * @params userId - The user ID
     * @params userName - The user's name
     * @params isTyping - Whether user is typing or not
     * @returns Observable that resolves when mutation completes
     */

    triggerTypingIndicator(chatId: string, userId: string, userName: string, isTyping: boolean): Promise<any> {
        console.log('⌨️ Triggering typing indicator mutation:', { chatId, userId, userName, isTyping });
        return API.graphql(
            graphqlOperation(TRIGGER_TYPING_INDICATOR, { chatId, userId, userName, isTyping })
        ) as any;
    }

    /**
     * Subscribe to typing indicator for specific chat
     * @params chatId - The chat ID to subscribe to
     * @returns Observable that emits typing indicator updates
     */

    subscribeToTypingIndicator(chatId: String): Observable<any> {
        console.log('📨 Subscribing to typing indicator for chat: ', chatId);
        return new Observable((observer) => {
            const graphqlResult = API.graphql(
                graphqlOperation(ON_TYPING_INDICATOR, { chatId })
            ) as any;

            if(graphqlResult && typeof graphqlResult.subscribe === 'function') {
                console.log('📨 Typing indicator subscription created');
                const subscription = graphqlResult.subscribe({
                    next: (data: any) => {
                        console.log('📨 Typing Indicator Subscription Data: ', data);
                        const indicator = data?.value?.data?.onTypingIndicator;
                        if(indicator) {
                            observer.next(indicator);
                        }
                    },
                    error: (error: any) => {
                        console.error('Typing Indicator Subscription Error: ', error);
                        observer.error(error);
                    },
                    complete: () => {
                        console.log('Typing Indicator subscription completed');
                        observer.complete();
                    }
                });

                return () => {
                    if(subscription && subscription.unsubscribe) {
                        subscription.unsubscribe();
                    }
                };
            }
            else {
                observer.error(new Error('Typing indicator subscription failed: Invalid response type'));
            }
        });
    }
}